import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Chip,
    Tabs,
    Tab,
    CircularProgress,
    Alert,
} from "@mui/material";
import { Edit, Close, Visibility } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import emailTemplateService from "../../services/emailTemplateService";
import PageHeader from "../../components/common/PageHeader";
import SkeletonLoader from "../../components/common/SkeletonLoader";

const extractVariables = (description = "") => {
    const matches = description.match(/\{\{\s*[\w.]+\s*\}\}/g);
    return matches ? [...new Set(matches)] : [];
};

const sampleDataFor = (variables) => {
    const samples = {
        firstName: "Alex",
        verifyUrl: "https://example.com/verify-email?token=sample",
        resetUrl: "https://example.com/reset-password?token=sample",
        trackingNumber: "WP-2026-0042",
        statusLabel: "Package Received",
        vendorName: "Amazon",
        packageDescription: "Sony headphones",
        trackUrl: "https://example.com/shipments/sample",
        invoiceNumber: "INV-2026-0042",
        amountDue: "245.00",
        amount: "245.00",
        viewInvoiceUrl: "https://example.com/dashboard/invoices/sample",
        dueDate: "Jan 15, 2026",
        balanceDue: "245.00",
        summaryItemsHtml: "<li>2 active shipment(s)</li><li>1 unread notification(s)</li>",
        pendingCount: "3",
        sailingDay: "Thursday",
        message: "Tuesday's 12:00 PM EST cutoff for Thursday's sailing is tomorrow — 3 received item(s) still aren't assigned to a sailing or consolidation.",
        tableRows: "<tr><td style=\"padding:4px 8px\">WP-2026-0012</td><td style=\"padding:4px 8px\">Sample item</td><td style=\"padding:4px 8px\">1Z999AA10123456784</td></tr>",
        moreNote: "",
        title: "Consolidation WP-CONS-0004 auto-sailed after its hold expired",
        trackingNumber: "WP-2026-0012",
        itemRows: "1Z999AA10123456784 - DR 1832741",
        invoiceLine: "<p>Invoice: WP-2026-0012-invoice.pdf</p>",
        senderName: "Alex Waypoint",
        shipmentCount: "3",
        sections: "<p><strong>Waypoint Tracking Number: WP-2026-0012</strong></p><p>1Z999AA10123456784 - DR 1832741</p>",
    };
    const data = {};
    variables.forEach((v) => {
        const key = v.replace(/\{\{\s*|\s*\}\}/g, "");
        data[key] = samples[key] || `[${key}]`;
    });
    return data;
};

const renderPreview = (str = "", data = {}) =>
    str.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, key) =>
        Object.prototype.hasOwnProperty.call(data, key) ? data[key] : match,
    );

const AdminEmailTemplates = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ subject: "", html: "", text: "", replyTo: "", notifyEmail: "" });
    const [saving, setSaving] = useState(false);
    const [previewTab, setPreviewTab] = useState(0); // 0 = edit, 1 = preview

    const loadTemplates = async () => {
        setLoading(true);
        try {
            const { data } = await emailTemplateService.getAll();
            setTemplates(data.data.templates || []);
        } catch (err) {
            enqueueSnackbar("Failed to load email templates", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTemplates();
    }, []);

    const openEdit = (template) => {
        setEditing(template);
        setForm({
            subject: template.subject,
            html: template.html,
            text: template.text || "",
            replyTo: template.replyTo || "",
            notifyEmail: template.notifyEmail || "",
        });
        setPreviewTab(0);
    };

    const variables = useMemo(
        () => (editing ? extractVariables(editing.description) : []),
        [editing],
    );
    const sampleData = useMemo(() => sampleDataFor(variables), [variables]);

    const handleSave = async () => {
        if (!form.subject || !form.html) {
            enqueueSnackbar("Subject and HTML body are required", {
                variant: "warning",
            });
            return;
        }
        if (form.replyTo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.replyTo)) {
            enqueueSnackbar("Reply-To must be a valid email address", { variant: "warning" });
            return;
        }
        if (form.notifyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.notifyEmail)) {
            enqueueSnackbar("Notify Email must be a valid email address", { variant: "warning" });
            return;
        }
        setSaving(true);
        try {
            await emailTemplateService.update(editing._id, form);
            enqueueSnackbar("Template updated", { variant: "success" });
            setEditing(null);
            loadTemplates();
        } catch (err) {
            enqueueSnackbar(
                err.response?.data?.message || "Failed to update template",
                { variant: "error" },
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Email Templates"
                subtitle="Edit the content of automated emails sent to customers"
            />

            {loading ? (
                <SkeletonLoader rows={3} />
            ) : (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 2,
                    }}
                >
                    {templates.map((t) => (
                        <Card key={t._id}>
                            <CardContent>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        mb: 1.5,
                                        gap: 1,
                                    }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography fontWeight={700} fontSize={15}>
                                            {t.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ fontFamily: "monospace" }}
                                        >
                                            {t.key}
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Edit />}
                                        onClick={() => openEdit(t)}
                                        sx={{ flexShrink: 0 }}
                                    >
                                        Edit
                                    </Button>
                                </Box>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    noWrap
                                    mb={1}
                                >
                                    Subject: {t.subject}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color={t.replyTo ? "text.secondary" : "text.disabled"}
                                    display="block"
                                    mb={1}
                                >
                                    Reply-To: {t.replyTo || "not set (defaults to FROM_EMAIL)"}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color={t.notifyEmail ? "text.secondary" : "text.disabled"}
                                    display="block"
                                    mb={1}
                                >
                                    Notify Email: {t.notifyEmail || "not set"}
                                </Typography>
                                {t.description && (
                                    <Typography variant="caption" color="text.disabled">
                                        {t.description}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}

            <Dialog
                open={!!editing}
                onClose={() => setEditing(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                {editing && (
                    <>
                        <DialogTitle
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography fontWeight={700} fontSize={16}>
                                Edit: {editing.name}
                            </Typography>
                            <IconButton size="small" onClick={() => setEditing(null)}>
                                <Close fontSize="small" />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers>
                            {variables.length > 0 && (
                                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                                    <Typography variant="caption" fontWeight={600} display="block">
                                        Available variables
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 0.5,
                                            mt: 0.5,
                                        }}
                                    >
                                        {variables.map((v) => (
                                            <Chip key={v} label={v} size="small" variant="outlined" />
                                        ))}
                                    </Box>
                                </Alert>
                            )}

                            <Tabs
                                value={previewTab}
                                onChange={(_, v) => setPreviewTab(v)}
                                sx={{ mb: 2, borderBottom: "1px solid", borderColor: "divider" }}
                            >
                                <Tab label="Edit" icon={<Edit fontSize="small" />} iconPosition="start" />
                                <Tab
                                    label="Preview"
                                    icon={<Visibility fontSize="small" />}
                                    iconPosition="start"
                                />
                            </Tabs>

                            {previewTab === 0 ? (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <TextField
                                        label="Subject"
                                        value={form.subject}
                                        onChange={(e) =>
                                            setForm((p) => ({ ...p, subject: e.target.value }))
                                        }
                                        fullWidth
                                    />
                                    <TextField
                                        label="Reply-To (optional)"
                                        type="email"
                                        placeholder="support@yourcompany.com"
                                        value={form.replyTo}
                                        onChange={(e) =>
                                            setForm((p) => ({ ...p, replyTo: e.target.value }))
                                        }
                                        helperText="When a recipient hits Reply, it goes here instead of the default sending address. Leave blank to use the default."
                                        fullWidth
                                    />
                                    <TextField
                                        label="Notify Email (optional)"
                                        type="email"
                                        placeholder="info@waypoint.ky"
                                        value={form.notifyEmail}
                                        onChange={(e) =>
                                            setForm((p) => ({ ...p, notifyEmail: e.target.value }))
                                        }
                                        helperText="CC'd on every email sent with this template — for notifying a shared inbox instead of a specific customer or admin. Leave blank for none."
                                        fullWidth
                                    />
                                    <TextField
                                        label="HTML Body"
                                        value={form.html}
                                        onChange={(e) =>
                                            setForm((p) => ({ ...p, html: e.target.value }))
                                        }
                                        fullWidth
                                        multiline
                                        minRows={12}
                                        sx={{ "& textarea": { fontFamily: "monospace", fontSize: 13 } }}
                                    />
                                    <TextField
                                        label="Plain Text Fallback"
                                        value={form.text}
                                        onChange={(e) =>
                                            setForm((p) => ({ ...p, text: e.target.value }))
                                        }
                                        fullWidth
                                        multiline
                                        minRows={2}
                                    />
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="overline" color="text.secondary" display="block" mb={1}>
                                        Subject preview
                                    </Typography>
                                    <Typography fontWeight={700} mb={2}>
                                        {renderPreview(form.subject, sampleData)}
                                    </Typography>
                                    <Typography variant="overline" color="text.secondary" display="block" mb={1}>
                                        Body preview (sample data)
                                    </Typography>
                                    <Box
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                            overflow: "hidden",
                                        }}
                                    >
                                        <iframe
                                            title="email-preview"
                                            srcDoc={renderPreview(form.html, sampleData)}
                                            sandbox=""
                                            style={{
                                                width: "100%",
                                                height: 400,
                                                border: "none",
                                                background: "white",
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ px: 3, py: 2 }}>
                            <Button onClick={() => setEditing(null)} variant="outlined">
                                Cancel
                            </Button>
                            <Button onClick={handleSave} variant="contained" disabled={saving}>
                                {saving ? <CircularProgress size={18} color="inherit" /> : "Save Template"}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default AdminEmailTemplates;